import { useMemo } from "react";
import styled from "styled-components";

import { Tabs } from "@otta/search/components/Tabs";
import { US_LOCATIONS, EU_LOCATIONS } from "@otta/search/constants/locations";
import { useUserLocation } from "@otta/search/hooks/useUserLocation";
import {
  Company,
  CompanyOtherBenefit,
  CompanyUserInfoDocument,
} from "@otta/search/schema";
import { useQuery } from "@otta/search/apollo";
import { pxToRem } from "@otta/design-tokens";
import { List, Text } from "@otta/design";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(16)};
`;

const getLocationLabel = (location: string) => {
  if (location === "ALL") return "Company-wide";
  if (location === "CA") return "Canadian";
  return location;
};

export const CompanyBenefitsList = ({
  company: { otherBenefits: benefits },
}: {
  company: Pick<Company.Fragment, "otherBenefits">;
}) => {
  const { data: userData } = useQuery(CompanyUserInfoDocument);
  const userLocationPreferences = useMemo(
    () => userData?.currentUser?.locationPreferences.map(l => l.location) ?? [],
    [userData?.currentUser?.locationPreferences]
  );

  const { country } = useUserLocation();

  const order = useMemo(() => {
    if (
      (userLocationPreferences.length &&
        userLocationPreferences.every(location =>
          US_LOCATIONS.has(location)
        )) ||
      country === "US"
    ) {
      return ["ALL", "US", "CA", "UK", "EU"];
    }

    if (
      userLocationPreferences.length &&
      userLocationPreferences.every(location => EU_LOCATIONS.has(location))
    ) {
      return ["ALL", "EU", "UK", "US", "CA"];
    }

    return ["ALL", "UK", "EU", "US", "CA"];
  }, [country, userLocationPreferences]);

  const benefitsByLocation = useMemo(() => {
    const groups = benefits.reduce<Record<string, CompanyOtherBenefit[]>>(
      (acc, benefit) => {
        const location = benefit.location ?? "ALL";

        if (acc[location]) {
          acc[location].push(benefit);
        } else {
          acc[location] = [benefit];
        }
        return acc;
      },
      {}
    );

    return Object.entries(groups).sort(
      ([a], [b]) => order.indexOf(a) - order.indexOf(b)
    );
  }, [benefits, order]);

  const tabs = useMemo(() => {
    return benefitsByLocation.map(([location, benefits]) => ({
      id: location,
      title: getLocationLabel(location),
      content: (
        <List type="bullet" size={0}>
          {benefits.map(b => (
            <li key={b.id} data-testid="company-benefit">
              {b.value}
            </li>
          ))}
        </List>
      ),
    }));
  }, [benefitsByLocation]);

  if (benefits.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <Text bold as="h3" size={1}>
        Benefits
      </Text>
      {benefitsByLocation.length > 1 ? <Tabs tabs={tabs} /> : tabs[0].content}
    </Wrapper>
  );
};
